import type { Plot, SignAnchor, StadiumCivicKind } from "@townsy/contracts";
import { YAW_NORTH, YAW_SOUTH, aabb } from "../geometry";
import { ARENA_INTERIORS } from "./arenas";
import { ASPEN_AVENUE_X } from "./sites";

/**
 * World v6, ADR-0044 decision 1: the Sports Park's five stadiums. Townsy-run CIVIC venues (never for sale) on Stadium
 * Way, the park's street, each with ONE shared interior room (venues/arenas). Coordinates are town metres (x east,
 * z north), like VENUE_SITES; the park itself (roads, square, decor, walls) is districts/sportsPark.ts.
 *
 *   - Stadium Way runs east-west along z = 226 (6 m road, 4 m sidewalks) from x 48 to 424.
 *   - Arena Avenue comes up from Market Street on Aspen Avenue's line (x = 240) and meets Stadium Way in a T-junction.
 *   - The three open-air stadiums stand on the north side facing south (toward the town and the overview camera):
 *     Townsy FC Park (west), Titans Field (at the head of Arena Avenue), Sluggers Ballpark (east). The two indoor arenas
 *     face them from the south side: Hoopers Arena (west of the square) and Blizzard Ice Center (east of Arena Avenue).
 *   - Each building is much bigger than a shop (12 x 16 m plots) or an attraction (48 x 36 m), 20..24 m high, with a
 *     storefront sign over its doors and a rooftop sign; the south row also has a back-to-back rooftop board facing
 *     south, toward the town and the overview camera (ADR-0023: rooftop boards are two-sided).
 *   - House numbers on Stadium Way count by position from its west end like Market Street's (two numbers per 14 m,
 *     odd on the north side, even on the south side); listed literally, sportsPark.test.ts checks they agree.
 */
export const STADIUM_WAY_NAME = "Stadium Way";
export const STADIUM_WAY_Z = 226;
export const STADIUM_WAY_X_MIN = 48;
export const STADIUM_WAY_X_MAX = 424;
export const ARENA_AVENUE_NAME = "Arena Avenue";
/** Arena Avenue continues Aspen Avenue's line north of Market Street. */
export const ARENA_AVENUE_X = ASPEN_AVENUE_X;
const ROAD_HALF = 3;
const SIDEWALK = 4;
/** The plots' front lines: the north side's at Stadium Way's north sidewalk edge, the south side's at its south one. */
export const STADIUM_NORTH_FRONT_Z = STADIUM_WAY_Z + ROAD_HALF + SIDEWALK; // 233
export const STADIUM_SOUTH_FRONT_Z = STADIUM_WAY_Z - ROAD_HALF - SIDEWALK; // 219

const ENTRANCE_SETBACK = 2.9;
const DOOR_HALF_WIDTH = 3;
/** The plot reaches this far beyond the building on either side and behind it (the front wall is 0.5 m behind the front line). */
const PLOT_MARGIN = 6;
const STOREFRONT_SIGN_Y = 8;

export interface StadiumSite {
  id: string;
  kind: StadiumCivicKind;
  name: string;
  /** The Townsy home team that plays there (ADR-0044 decision 2). */
  homeTeam: string;
  /** Which side of Stadium Way it stands on (north: facing south; south: facing north). */
  side: "north" | "south";
  cx: number;
  address: string;
  description: string;
  /** Exterior shell: footprint (width along the street, depth away from it) and height, metres. */
  building: { width: number; depth: number; height: number };
  rooftopSign: { width: number; height: number };
  storefrontSign: { width: number; height: number };
}

const STADIUM_ROOF_SIGN = { width: 40, height: 10 } as const;
const STADIUM_FRONT_SIGN = { width: 24, height: 4.8 } as const;
const ARENA_ROOF_SIGN = { width: 30, height: 8 } as const;
const ARENA_FRONT_SIGN = { width: 18, height: 3.6 } as const;
// ADR-0048: predictions need an account; watching, the seats and cheering are open to everyone.
const PLAY = "Take a seat in the stands, cheer for your team and, signed up (18+), predict the winner with free play chips (just for fun, no value).";

/** The five stadiums, in property order (v-08..v-12, ADR-0044 decision 1). */
export const STADIUM_SITES: readonly StadiumSite[] = [
  {
    id: "v-08",
    kind: "football_stadium",
    name: "Titans Field",
    homeTeam: "Townsy Titans",
    side: "north",
    cx: ARENA_AVENUE_X,
    address: "29 Stadium Way",
    description: `American football all day: the Townsy Titans take on a visiting team, live. ${PLAY}`,
    building: { width: 104, depth: 76, height: 24 },
    rooftopSign: STADIUM_ROOF_SIGN,
    storefrontSign: STADIUM_FRONT_SIGN,
  },
  {
    id: "v-09",
    kind: "soccer_stadium",
    name: "Townsy FC Park",
    homeTeam: "Townsy FC",
    side: "north",
    cx: 116,
    address: "11 Stadium Way",
    description: `Soccer all day: Townsy FC take on a visiting team, live. ${PLAY}`,
    building: { width: 100, depth: 72, height: 22 },
    rooftopSign: STADIUM_ROOF_SIGN,
    storefrontSign: STADIUM_FRONT_SIGN,
  },
  {
    id: "v-10",
    kind: "baseball_stadium",
    name: "Sluggers Ballpark",
    homeTeam: "Townsy Sluggers",
    side: "north",
    cx: 360,
    address: "45 Stadium Way",
    description: `Baseball all day: the Townsy Sluggers take on a visiting team, live. ${PLAY}`,
    building: { width: 92, depth: 92, height: 20 },
    rooftopSign: STADIUM_ROOF_SIGN,
    storefrontSign: STADIUM_FRONT_SIGN,
  },
  {
    id: "v-11",
    kind: "basketball_arena",
    name: "Hoopers Arena",
    homeTeam: "Townsy Hoopers",
    side: "south",
    cx: 139,
    address: "14 Stadium Way",
    description: `Basketball all day: the Townsy Hoopers take on a visiting team, live. ${PLAY}`,
    building: { width: 68, depth: 54, height: 22 },
    rooftopSign: ARENA_ROOF_SIGN,
    storefrontSign: ARENA_FRONT_SIGN,
  },
  {
    id: "v-12",
    kind: "hockey_arena",
    name: "Blizzard Ice Center",
    homeTeam: "Townsy Blizzard",
    side: "south",
    cx: 299,
    address: "36 Stadium Way",
    description: `Ice hockey all day: the Townsy Blizzard take on a visiting team, live. ${PLAY}`,
    building: { width: 72, depth: 56, height: 22 },
    rooftopSign: ARENA_ROOF_SIGN,
    storefrontSign: ARENA_FRONT_SIGN,
  },
];

/** House number on Stadium Way by position (as Market Street's): two numbers per 14 m from its west end, odd north. */
export function stadiumWayNumber(x: number, side: "north" | "south"): number {
  return 2 * Math.round((x - STADIUM_WAY_X_MIN) / 14) + (side === "north" ? 1 : 0);
}

/** The plot of a stadium site (CIVIC, never purchasable), fronting onto Stadium Way. */
export function stadiumPlot(site: StadiumSite, districtId: string): Plot {
  const B = site.building;
  const north = site.side === "north";
  const out = north ? -1 : 1; // from the front wall toward the street
  const frontZ = north ? STADIUM_NORTH_FRONT_Z : STADIUM_SOUTH_FRONT_Z;
  const wallZ = frontZ - out * 0.5;
  const plotDepth = 0.5 + B.depth + PLOT_MARGIN;
  const halfW = B.width / 2 + PLOT_MARGIN;
  const facing = north ? YAW_SOUTH : YAW_NORTH;
  const roofY = B.height + site.rooftopSign.height / 2 + 0.3;
  const signs: SignAnchor[] = [
    { kind: "storefront", position: { x: site.cx, y: STOREFRONT_SIGN_Y, z: wallZ + out * 0.06 }, facing, width: site.storefrontSign.width, height: site.storefrontSign.height },
    { kind: "skyline", position: { x: site.cx, y: roofY, z: wallZ - out * 1.2 }, facing, width: site.rooftopSign.width, height: site.rooftopSign.height },
  ];
  // The south row backs onto the town: a back-to-back board faces south, toward Central and the overview camera.
  if (!north) signs.push({ kind: "skyline", position: { x: site.cx, y: roofY, z: wallZ - out * 1.5 }, facing: YAW_SOUTH, width: site.rooftopSign.width, height: site.rooftopSign.height });
  return {
    id: site.id,
    districtId,
    type: "CIVIC",
    address: site.address,
    bounds: north ? aabb(site.cx - halfW, frontZ, site.cx + halfW, frontZ + plotDepth) : aabb(site.cx - halfW, frontZ - plotDepth, site.cx + halfW, frontZ),
    buildBounds: north ? aabb(site.cx - B.width / 2, wallZ, site.cx + B.width / 2, wallZ + B.depth) : aabb(site.cx - B.width / 2, wallZ - B.depth, site.cx + B.width / 2, wallZ),
    entrance: { position: { x: site.cx, z: wallZ + out * ENTRANCE_SETBACK }, facing: north ? YAW_NORTH : YAW_SOUTH },
    door: { position: { x: site.cx, z: wallZ }, direction: { x: 0, z: -out }, halfWidth: DOOR_HALF_WIDTH },
    facing,
    civic: { kind: site.kind, name: site.name, description: site.description },
    // The stadium's one shared room (ARENA_INTERIORS; its id is the template the manifest registers).
    interiorTemplate: ARENA_INTERIORS[site.kind].id,
    signAnchors: signs,
    decorationAnchors: [],
    heightBand: 0,
  };
}

/** The five stadium plots, in property order. */
export function stadiumPlots(districtId: string): Plot[] {
  return STADIUM_SITES.map((s) => stadiumPlot(s, districtId));
}
